import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faClose } from '@fortawesome/pro-light-svg-icons';

// services
import VendorsService from '../../../../../services/data/vendors';

// props

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import PermissionsService from '../../../../../services/permissions';
import ErrorService from '../../../../../services/general/error';
import NoAccess from '../../../../uiframeworks/noAccess';
import Text from '../../../../typography/text';
import { IVendorResourceProps } from '../../../../../props/data/vendors';
import Label from '../../../../typography/label';
import ValidationService from '../../../../../services/validation';

type InactivateVendorFormProps = {
    vendorId: string;
    onDismissed(refresh?: boolean, vendor?: IVendorResourceProps): void;
}

const InactivateVendorForm: React.FC<InactivateVendorFormProps> = (props: InactivateVendorFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [vendor, setVendor] = React.useState<IVendorResourceProps | undefined>();
    const [justification, setJustification] = React.useState<string>("");
    const [error, setError] = React.useState<any>({});

    let hasPermission = PermissionsService.hasPermission(['vendors.delete.all'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _vendor = await VendorsService.get(props.vendorId);

        setVendor(_vendor);
        setLoaded(true);
    }

    const _onSubmit = async () => {
        try {
            if (window.confirm("Are you sure to set this vendor as inactive?")) {
                setSubmitting(true);
                const fd = new FormData();
                fd.append("justification", justification);

                await VendorsService.inactivate(props.vendorId, fd);

                banner.add({
                    key: 'inactivate_vendor_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Vendor "${vendor?.name}" is now inactive`
                });
                props.onDismissed(true);
            }
        } catch (e) {
            banner.add({
                key: 'inactivate_vendor_error',
                variant: 'error',
                icon: faClose,
                text: ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    }

    return <Panel headerText={"Inactivate Vendor"}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton text={"Delete"} disabled={error.justification || (justification || "").trim() === ""} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Inactivating vendor ..."} /> : null}
            </Stack>;
        }}>
        <Stack>
            {!loaded ? <Stack horizontalAlign={"baseline"} styles={{ root: { marginTop: 20 } }}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && vendor && hasPermission ? <>
                    <Stack tokens={{ childrenGap: 20 }}>
                        <Text>Are you sure you want to set vendor with details below as inactive?</Text>
                        <Stack>
                            <Label size={'xsmall'}>Name</Label>
                            <Text size={'small'}>{vendor.name}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Address</Label>
                            <Text size={'small'}>{vendor.address || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Phone number</Label>
                            <Text size={'small'}>{vendor.phoneNumber || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>NPWP</Label>
                            <Text size={'small'}>{vendor.npwp || "-"}</Text>
                        </Stack>
                        <Stack className={'divider'} />
                        <Stack>
                            <Label size={'xsmall'}>Owner name</Label>
                            <Text size={'small'}>{vendor.ownerName || "-"}</Text>
                        </Stack>
                        <Stack>
                            <Label size={'xsmall'}>Onwer ID number</Label>
                            <Text size={'small'}>{vendor.ownerIdentification || "-"}</Text>
                        </Stack>
                        <Stack className={'divider'}></Stack>
                        <TextField label={'Justification'}
                            value={justification}
                            multiline
                            autoAdjustHeight
                            resizable={false}
                            errorMessage={error.justification}
                            onChange={(evt, value) => {
                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 1000 });
                                error.justification = validation.message;

                                setJustification(value || "");
                                setError({ ...error });
                            }} />
                    </Stack>
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default InactivateVendorForm;
